import type { TrucklistFilterData } from '#app/broker/trucklist/utils/utils'
import type { EquipmentTypeResponseItem } from '#app/shared/types/EquipmentTypes'
import { deduplicateEquipmentTypes } from '#app/shared/utils/equipment'
import type { AuthStateType } from './userMachine'

/* APP STATE STORE UTILS */

// Key for the filters in the local storage
export const FILTERS_KEY = 'trucklistFilters'

/**
 * Loads saved filters from local storage.
 * @returns {Partial<TrucklistFilterData>} The saved filters or an empty object if loading fails.
 */
export const loadSavedFilters = (): Partial<TrucklistFilterData> => {
  try {
    const savedFilters = JSON.parse(localStorage.getItem(FILTERS_KEY) || '{}')

    let equipmentTypes = savedFilters.equipmentTypes

    // Check if equipmentTypes exists and is an array
    if (equipmentTypes && Array.isArray(equipmentTypes)) {
      // Filter out invalid equipment types
      equipmentTypes = equipmentTypes.filter(
        (eq: string | EquipmentTypeResponseItem) => eq !== null && eq !== undefined
      )

      const uniqueEquipmentTypes = deduplicateEquipmentTypes(equipmentTypes)
      savedFilters.equipmentTypes = uniqueEquipmentTypes
    } else {
      // If equipmentTypes is not valid, set it to empty array
      savedFilters.equipmentTypes = []
    }

    return savedFilters
  } catch (error) {
    console.error('Failed to load filters from local storage', error)
    return {}
  }
}

/**
 * Saves filters to local storage.
 * @param {Partial<TrucklistFilterData>} filters - The filters to save.
 */
export const saveFiltersToLocalStorage = (
  filters: Partial<TrucklistFilterData>
): void => {
  try {
    const uniqueEquipmentTypes = deduplicateEquipmentTypes(
      filters.equipmentTypes || []
    )
    filters.equipmentTypes = uniqueEquipmentTypes
    localStorage.setItem(FILTERS_KEY, JSON.stringify(filters))
  } catch (error) {
    console.error('Failed to save filters to local storage', error)
  }
}

/**
 * Extracts specific filters to save to local storage.
 * @param {TrucklistFilterData} filters - The filters to extract from.
 * @returns {Object} An object containing the filters to save.
 */
export const getFiltersToSave = (
  filters: TrucklistFilterData
): {
  pickupRadius: number
  dropoffRadius: number
  equipmentTypes: EquipmentTypeResponseItem[]
} => ({
  pickupRadius: filters.pickupRadius,
  dropoffRadius: filters.dropoffRadius,
  equipmentTypes: filters.equipmentTypes
})

export enum OnboardingStepNeeded {
  NamePhone = 'NamePhone',
  Filters = 'Filters',
  CarrierOnboarding = 'CarrierOnboarding'
}

/**
 * Checks if the user needs to go through onboarding.
 * @param {UserProfile} context - The user's authentication context.
 * @returns {OnboardingStepNeededEnum | null} Step needed for onboarding.
 */
export const checkOnboardingStepNeeded = (
  authState: AuthStateType
): OnboardingStepNeeded | null => {
  // For carriers, check if they need to complete onboarding based on approval status
  if (
    !authState.authContext?.authenticatedUser?.is_approved
    && !authState.isCarrier && !authState.isBroker
  ) {
    return OnboardingStepNeeded.CarrierOnboarding
  }

  // For brokers, check profile completion
  if (!authState.userProfile) {
    return null
  }
  const userProfile = authState.userProfile
  if (
    authState.isBroker
    && (!userProfile.first_name
      || !userProfile.last_name
      || !userProfile.phone
      || !userProfile.equipment_filter
      || !userProfile.equipment_filter.length)
  ) {
    return OnboardingStepNeeded.NamePhone
  }

  return null
}

/* END APP STATE STORE UTILS */
